import { useCallback, useSyncExternalStore } from 'react';
import { isViewportResizing, onViewportResizeIdle } from '@/lib/viewportResize';

const layoutSnapshot = new Map<string, boolean>();

function readMedia(query: string) {
  return window.matchMedia(query).matches;
}

function getLayoutSnapshot(query: string) {
  if (!layoutSnapshot.has(query)) {
    layoutSnapshot.set(query, typeof window !== 'undefined' && readMedia(query));
  }
  return layoutSnapshot.get(query)!;
}

export function useMediaQuery(query: string, debounceMs = 0): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const media = window.matchMedia(query);

      if (debounceMs <= 0) {
        media.addEventListener('change', onStoreChange);
        return () => media.removeEventListener('change', onStoreChange);
      }

      // Seen is per subscriber. The shared snapshot is written by whichever
      // listener runs first, so a shared equality check would skip the rest.
      let seen = getLayoutSnapshot(query);
      const flush = () => {
        if (isViewportResizing()) return;
        const next = readMedia(query);
        layoutSnapshot.set(query, next);
        if (seen === next) return;
        seen = next;
        onStoreChange();
      };

      media.addEventListener('change', flush);
      const stopIdle = onViewportResizeIdle(flush);
      return () => {
        media.removeEventListener('change', flush);
        stopIdle();
      };
    },
    [query, debounceMs],
  );

  const getSnapshot = useCallback(() => {
    if (debounceMs <= 0) return readMedia(query);
    return getLayoutSnapshot(query);
  }, [query, debounceMs]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export function resetLayoutMediaSnapshotsForTests() {
  layoutSnapshot.clear();
}
